import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Users,
  Search,
  Plus,
  Eye,
  Pencil,
  Send,
  RefreshCw,
  Loader2,
  X,
  Save,
  Copy,
  ExternalLink,
} from 'lucide-react';
import GrowthTabNav from './GrowthTabNav';
import {
  getGrowthProspects,
  createGrowthProspect,
  updateGrowthProspect,
  inviteGrowthProspect,
  getGrowthProspectById,
  getPortalConfig,
} from '../../Services/GrowthService';
import {
  ACCESS_TRACK_LABEL,
  AccessTrack,
  accessTracksForRole,
  PortalConfig,
  Prospect,
  ProspectRequest,
  GrowthInvitation,
  TargetRole,
} from '../../Types/GrowthType';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import Pagination from '../../../Shared/Pagination/Pagination';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';

const PAGE_SIZE = 10;

const labelClass =
  'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

const PROSPECT_STATUSES = [
  'NEW',
  'INTERESTED',
  'INVITATION_SENT',
  'INVITATION_OPENED',
  'INVITATION_CLICKED',
  'REGISTERED',
  'APPROVED',
  'REJECTED',
  'DO_NOT_CONTACT',
];

const INVITATION_STATUSES = [
  'CREATED',
  'SENT',
  'DELIVERED',
  'OPENED',
  'CLICKED',
  'ACCEPTED',
  'REGISTERED',
  'EXPIRED',
];

const statusBadge = (status?: string | null) => {
  const s = (status || '').toUpperCase();
  if (s === 'APPROVED' || s === 'REGISTERED' || s === 'ACCEPTED' || s === 'CLICKED' || s === 'OPENED' || s === 'DELIVERED' || s === 'SENT')
    return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400';
  if (s === 'REJECTED' || s === 'DO_NOT_CONTACT' || s === 'EXPIRED')
    return 'border-rose-500/30 bg-rose-500/10 text-rose-400';
  if (s === 'INTERESTED' || s === 'INVITATION_SENT' || s === 'INVITATION_OPENED' || s === 'INVITATION_CLICKED' || s === 'CREATED')
    return 'border-amber-500/30 bg-amber-500/10 text-amber-400';
  return 'border-slate-600/40 bg-slate-700/20 text-slate-300';
};

const emptyProspectForm: ProspectRequest = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  linkedInUrl: '',
  company: '',
  currentTitle: '',
  experienceYears: undefined,
  primaryTechnologyArea: '',
  message: '',
  source: '',
  medium: '',
  campaign: '',
  content: '',
  referralCode: '',
  prospectStatus: 'NEW',
  notes: '',
  consentToContact: true,
  targetRole: 'CANDIDATE',
};

function GrowthProspectsComponent() {
  const [pageData, setPageData] = useState<PaginationResponse<Prospect> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [invitationStatusFilter, setInvitationStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [titleFilter, setTitleFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<Prospect | null>(null);
  const [form, setForm] = useState<ProspectRequest>(emptyProspectForm);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteTtl, setInviteTtl] = useState(72);
  const [createdInvitation, setCreatedInvitation] = useState<GrowthInvitation | null>(null);
  const [portalConfig, setPortalConfig] = useState<PortalConfig | null>(null);
  const [inviteTargetRole, setInviteTargetRole] = useState<TargetRole>('CANDIDATE');
  const [inviteAccessTrack, setInviteAccessTrack] = useState<AccessTrack>('NORMAL');

  const showAlert = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message, autoCloseMs: 5000, onClose: () => setAlert(null) });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGrowthProspects({
        page,
        size: PAGE_SIZE,
        status: statusFilter || undefined,
        invitationStatus: invitationStatusFilter || undefined,
        source: sourceFilter || undefined,
        title: titleFilter || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      if (response.success && response.data) {
        setPageData(response.data);
      }
    } catch (err) {
      console.error('Failed to load prospects', err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, invitationStatusFilter, sourceFilter, titleFilter, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, invitationStatusFilter, sourceFilter, titleFilter, fromDate, toDate]);

  // Track/role availability is portal-config driven, so read it once before inviting.
  useEffect(() => {
    const loadConfig = async () => {
      const response = await getPortalConfig();
      if (response.success && response.data) setPortalConfig(response.data);
    };
    loadConfig();
  }, []);

  /** Resets the track to the first one the selected role is actually allowed to use. */
  const syncAccessTrackForRole = (role: TargetRole, preferred?: AccessTrack) => {
    const allowed = accessTracksForRole(role);
    const next = preferred && allowed.some((o) => o.value === preferred) ? preferred : allowed[0].value;
    setInviteAccessTrack(next);
    return next;
  };

  const isTrackEnabled = (track: AccessTrack) => {
    if (!portalConfig) return true;
    if (track === 'EARLY_ACCESS') return portalConfig.earlyAccessEnabled;
    if (track === 'CANDIDATE_REFERRAL') return portalConfig.candidateReferralEnabled;
    if (track === 'ADMIN_REFERRAL') return portalConfig.adminReferralEnabled;
    return true;
  };

  const setFormValue = (key: keyof ProspectRequest) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const openCreate = () => {
    setForm(emptyProspectForm);
    setIsCreateOpen(true);
  };

  const openEdit = async (prospect: Prospect) => {
    setSelected(prospect);
    setForm({
      firstName: prospect.firstName || '',
      lastName: prospect.lastName || '',
      email: prospect.email || '',
      phone: prospect.phone || '',
      linkedInUrl: prospect.linkedInUrl || '',
      company: prospect.company || '',
      currentTitle: prospect.currentTitle || '',
      experienceYears: prospect.experienceYears ?? undefined,
      primaryTechnologyArea: prospect.primaryTechnologyArea || '',
      message: prospect.message || '',
      source: prospect.source || '',
      medium: prospect.sourceMedium || '',
      campaign: prospect.sourceCampaign || '',
      content: prospect.sourceContent || '',
      referralCode: prospect.sourceReferralCode || '',
      prospectStatus: prospect.prospectStatus,
      notes: prospect.notes || '',
      consentToContact: prospect.consentToContact ?? true,
    });
    setIsEditOpen(true);
  };

  const openDetail = async (prospect: Prospect) => {
    setIsDetailOpen(true);
    setSelected(prospect);
    try {
      const response = await getGrowthProspectById(prospect.id);
      if (response.success && response.data) setSelected(response.data);
    } catch {
      // keep the row data
    }
  };

  const openInvite = (prospect: Prospect) => {
    setSelected(prospect);
    setInviteMessage('');
    setInviteTtl(72);
    setCreatedInvitation(null);
    const role: TargetRole = prospect.targetRole === 'RECRUITER' ? 'RECRUITER' : 'CANDIDATE';
    setInviteTargetRole(role);
    setInviteAccessTrack(syncAccessTrackForRole(role));
    setIsInviteOpen(true);
  };

  const handleCreate = async () => {
    if (!form.email && !form.phone) {
      showAlert('error', 'Missing Contact', 'Provide at least an email or phone number.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await createGrowthProspect(form);
      if (response.success) {
        showAlert('success', 'Success...!', 'Prospect created successfully.');
        setIsCreateOpen(false);
        fetchData();
      } else {
        showAlert('error', 'Create Error', response.message || 'Failed to create prospect.');
      }
    } catch {
      showAlert('error', 'Create Error', 'Failed to create prospect.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const response = await updateGrowthProspect(selected.id, form);
      if (response.success) {
        showAlert('success', 'Success...!', 'Prospect updated successfully.');
        setIsEditOpen(false);
        fetchData();
      } else {
        showAlert('error', 'Update Error', response.message || 'Failed to update prospect.');
      }
    } catch {
      showAlert('error', 'Update Error', 'Failed to update prospect.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvite = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const response = await inviteGrowthProspect(selected.id, {
        message: inviteMessage || undefined,
        ttlHours: inviteTtl || undefined,
        targetRole: inviteTargetRole,
        accessTrack: inviteAccessTrack,
      });
      if (response.success && response.data) {
        setCreatedInvitation(response.data);
        showAlert('success', 'Invitation Sent', 'Growth invitation generated successfully.');
        fetchData();
      } else {
        showAlert('error', 'Invite Error', response.message || 'Failed to generate invitation.');
      }
    } catch {
      showAlert('error', 'Invite Error', 'Failed to generate invitation.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      showAlert('success', 'Copied', 'Invitation link copied to clipboard.');
    } catch {
      showAlert('error', 'Copy Failed', 'Unable to copy the link.');
    }
  };

  const content = pageData?.content || [];
  const totalElements = pageData?.totalElements || 0;
  const totalPages = pageData?.totalPages || 0;

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <GrowthTabNav />

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Users size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Prospects</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Captured leads &amp; manually added prospects
            </p>
          </div>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400"
          >
            <Plus size={16} />
            Add Prospect
          </button>
          <button
            onClick={fetchData}
            title="Refresh"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {alert && (
        <div className="mb-4">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={titleFilter}
              onChange={(e) => setTitleFilter(e.target.value)}
              placeholder="Search by title / keyword..."
              className={`${fieldClass} pl-9`}
            />
          </div>
          <div>
            <label className={labelClass}>Source</label>
            <input value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} placeholder="e.g. linkedin" className={fieldClass} />
          </div>
          <div>
            <label className={labelClass}>Prospect Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={fieldClass}>
              <option value="">All statuses</option>
              {PROSPECT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Invitation Status</label>
            <select
              value={invitationStatusFilter}
              onChange={(e) => setInvitationStatusFilter(e.target.value)}
              className={fieldClass}
            >
              <option value="">All</option>
              {INVITATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={fieldClass} />
          </div>
          <div>
            <label className={labelClass}>To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={fieldClass} />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
        <table className="w-full min-w-[1000px] text-left">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60">
              {['Name', 'Email', 'Title / Company', 'Source', 'Role', 'Prospect Status', 'Invitation', 'Added', 'Actions'].map((h) => (
                <th key={h} className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {content.map((prospect, idx) => (
              <motion.tr
                key={prospect.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="transition hover:bg-slate-900/70"
              >
                <td className="px-5 py-4 text-sm font-bold text-slate-100">
                  {prospect.firstName} {prospect.lastName}
                </td>
                <td className="px-5 py-4 text-sm font-semibold text-cyan-400">{prospect.email || '—'}</td>
                <td className="px-5 py-4">
                  <span className="text-sm font-semibold text-slate-300">{prospect.currentTitle || '—'}</span>
                  {prospect.company && <span className="block text-[11px] font-semibold text-slate-600">{prospect.company}</span>}
                </td>
                <td className="px-5 py-4 text-sm font-semibold text-slate-400">{prospect.source || '—'}</td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-flex rounded-lg border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
                      prospect.targetRole === 'RECRUITER'
                        ? 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-400'
                        : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400'
                    }`}
                  >
                    {prospect.targetRole === 'RECRUITER' ? 'Recruiter' : 'Candidate'}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${statusBadge(prospect.prospectStatus)}`}>
                    {prospect.prospectStatus}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${statusBadge(prospect.invitationStatus)}`}>
                    {prospect.invitationStatus || 'NONE'}
                  </span>
                </td>
                <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                  {new Date(prospect.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => openDetail(prospect)}
                      title="View"
                      className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-2 text-sky-400 transition hover:bg-sky-500/20"
                    >
                      <Eye size={15} />
                    </button>
                    <button
                      onClick={() => openEdit(prospect)}
                      title="Edit"
                      className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-2 text-cyan-400 transition hover:bg-cyan-500/20"
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      onClick={() => openInvite(prospect)}
                      title="Send Invitation"
                      className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-2 text-amber-400 transition hover:bg-amber-500/20"
                    >
                      <Send size={15} />
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
            {content.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                  No prospects match the current filters.
                </td>
              </tr>
            )}
            {loading && content.length === 0 && (
              <tr>
                <td colSpan={9} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                  <RefreshCw className="mx-auto h-6 w-6 animate-spin text-cyan-500" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalElements > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
          <Pagination
            page={page}
            size={PAGE_SIZE}
            totalElements={totalElements}
            totalPages={totalPages}
            first={pageData?.first ?? page === 0}
            last={pageData?.last ?? false}
            hasNext={pageData?.hasNext ?? false}
            hasPrevious={pageData?.hasPrevious ?? page > 0}
            setPage={setPage}
          />
        </div>
      )}

      {/* Prospect form modal (create / edit) */}
      <AnimatePresence>
        {(isCreateOpen || isEditOpen) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => {
              setIsCreateOpen(false);
              setIsEditOpen(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            >
              <div className="mb-6 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black text-slate-100">
                    {isCreateOpen ? 'Add Prospect' : `Edit ${selected?.firstName || ''} ${selected?.lastName || ''}`}
                  </h2>
                  <p className="text-xs font-semibold text-slate-400">Prospect details &amp; attribution</p>
                </div>
                <button
                  onClick={() => {
                    setIsCreateOpen(false);
                    setIsEditOpen(false);
                  }}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>First Name</label>
                  <input value={form.firstName || ''} onChange={setFormValue('firstName')} className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>Last Name</label>
                  <input value={form.lastName || ''} onChange={setFormValue('lastName')} className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>Email</label>
                  <input type="email" value={form.email || ''} onChange={setFormValue('email')} className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input value={form.phone || ''} onChange={setFormValue('phone')} className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>LinkedIn URL</label>
                  <input value={form.linkedInUrl || ''} onChange={setFormValue('linkedInUrl')} className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>Company</label>
                  <input value={form.company || ''} onChange={setFormValue('company')} className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>Current Title</label>
                  <input value={form.currentTitle || ''} onChange={setFormValue('currentTitle')} className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>Experience (Years)</label>
                  <input
                    type="number"
                    value={form.experienceYears ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, experienceYears: e.target.value ? Number(e.target.value) : undefined }))}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Technology Area</label>
                  <input value={form.primaryTechnologyArea || ''} onChange={setFormValue('primaryTechnologyArea')} className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <select value={form.prospectStatus || 'NEW'} onChange={setFormValue('prospectStatus')} className={fieldClass}>
                    {PROSPECT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Source</label>
                  <input value={form.source || ''} onChange={setFormValue('source')} placeholder="e.g. linkedin" className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>Medium</label>
                  <input value={form.medium || ''} onChange={setFormValue('medium')} className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>Campaign</label>
                  <input value={form.campaign || ''} onChange={setFormValue('campaign')} className={fieldClass} />
                </div>
                <div>
                  <label className={labelClass}>Referral Code</label>
                  <input value={form.referralCode || ''} onChange={setFormValue('referralCode')} className={fieldClass} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Message</label>
                  <textarea rows={2} value={form.message || ''} onChange={setFormValue('message')} className={fieldClass} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Notes</label>
                  <textarea rows={2} value={form.notes || ''} onChange={setFormValue('notes')} className={fieldClass} />
                </div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                  <input
                    type="checkbox"
                    checked={form.consentToContact ?? true}
                    onChange={(e) => setForm((prev) => ({ ...prev, consentToContact: e.target.checked }))}
                    className="h-4 w-4 accent-cyan-500"
                  />
                  Consent to contact
                </label>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setIsCreateOpen(false);
                    setIsEditOpen(false);
                  }}
                  className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={isCreateOpen ? handleCreate : handleUpdate}
                  disabled={submitting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cyan-500 py-3 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {isCreateOpen ? 'Create' : 'Update'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail modal */}
      <AnimatePresence>
        {isDetailOpen && selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsDetailOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            >
              <div className="mb-5 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black text-slate-100">
                    {selected.firstName} {selected.lastName}
                  </h2>
                  <p className="text-xs font-semibold text-slate-400">{selected.email}</p>
                </div>
                <button
                  onClick={() => setIsDetailOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  { label: 'Phone', value: selected.phone || '—' },
                  { label: 'Company', value: selected.company || '—' },
                  { label: 'Current Title', value: selected.currentTitle || '—' },
                  { label: 'Experience', value: selected.experienceYears != null ? `${selected.experienceYears} years` : '—' },
                  { label: 'Technology Area', value: selected.primaryTechnologyArea || '—' },
                  { label: 'Source', value: selected.source || '—' },
                  { label: 'Medium', value: selected.sourceMedium || '—' },
                  { label: 'Campaign', value: selected.sourceCampaign || '—' },
                  { label: 'Referral Code', value: selected.sourceReferralCode || '—' },
                  { label: 'Captured By', value: selected.capturedBy || '—' },
                  { label: 'Consent', value: selected.consentToContact ? 'Yes' : 'No' },
                  { label: 'User Id', value: selected.userId || '—' },
                  {
                    label: 'Target Role',
                    value: selected.targetRole === 'RECRUITER' ? 'Recruiter (Hiring Partner)' : 'Candidate (Executive)',
                  },
                ].map((item) => (
                  <div key={item.label} className="rounded-xl bg-slate-950/60 px-4 py-3">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{item.label}</p>
                    <p className="mt-1 text-sm font-semibold text-slate-200">{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${statusBadge(selected.prospectStatus)}`}>
                  {selected.prospectStatus}
                </span>
                <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${statusBadge(selected.invitationStatus)}`}>
                  Invite: {selected.invitationStatus || 'NONE'}
                </span>
                <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${statusBadge(selected.registrationStatus)}`}>
                  Registration: {selected.registrationStatus || 'NONE'}
                </span>
              </div>

              {selected.message && (
                <div className="mt-4 rounded-xl bg-slate-950/60 px-4 py-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Message</p>
                  <p className="mt-1 text-sm font-medium text-slate-300">{selected.message}</p>
                </div>
              )}
              {selected.notes && (
                <div className="mt-3 rounded-xl bg-slate-950/60 px-4 py-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Notes</p>
                  <p className="mt-1 text-sm font-medium text-slate-300">{selected.notes}</p>
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setIsDetailOpen(false);
                    openInvite(selected);
                  }}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 py-3 text-sm font-black text-amber-400 transition hover:bg-amber-500/20"
                >
                  <Send size={16} />
                  Send Invitation
                </button>
                <button
                  onClick={() => {
                    setIsDetailOpen(false);
                    openEdit(selected);
                  }}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cyan-500 py-3 text-sm font-black text-slate-950 transition hover:bg-cyan-400"
                >
                  <Pencil size={16} />
                  Edit
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite modal */}
      <AnimatePresence>
        {isInviteOpen && selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsInviteOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            >
              <div className="mb-5 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black text-slate-100">Send Growth Invitation</h2>
                  <p className="text-xs font-semibold text-slate-400">
                    {selected.firstName} {selected.lastName} · {selected.email}
                  </p>
                </div>
                <button
                  onClick={() => setIsInviteOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              {createdInvitation ? (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-emerald-400">Invitation created</p>
                  <div className="mt-3 flex items-center gap-2 rounded-xl bg-slate-950/60 px-3 py-2">
                    <span className="flex-1 truncate text-xs font-semibold text-slate-200">
                      {createdInvitation.invitationUrl || createdInvitation.token}
                    </span>
                    <button
                      onClick={() =>
                        copyLink(createdInvitation.invitationUrl || `${createdInvitation.token}`)
                      }
                      className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-1.5 text-cyan-400 transition hover:bg-cyan-500/20"
                      title="Copy link"
                    >
                      <Copy size={14} />
                    </button>
                    {createdInvitation.invitationUrl && (
                      <a
                        href={createdInvitation.invitationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-1.5 text-cyan-400 transition hover:bg-cyan-500/20"
                        title="Open link"
                      >
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                  <p className="mt-3 text-[11px] font-semibold text-emerald-300">
                    Status: {createdInvitation.status} · Role:{' '}
                    {createdInvitation.targetRole || 'CANDIDATE'} · Track:{' '}
                    {createdInvitation.accessTrack
                      ? ACCESS_TRACK_LABEL[createdInvitation.accessTrack]
                      : 'Default'}{' '}
                    · Expires:{' '}
                    {createdInvitation.expiresAt ? new Date(createdInvitation.expiresAt).toLocaleString() : '—'}
                  </p>
                  {createdInvitation.waitlistStatus && (
                    <p className="mt-1 text-[11px] font-semibold text-slate-400">
                      Waitlist status on registration: {createdInvitation.waitlistStatus}
                    </p>
                  )}
                  <button
                    onClick={() => setIsInviteOpen(false)}
                    className="mt-4 w-full rounded-xl bg-emerald-500 py-2.5 text-sm font-black text-slate-950 transition hover:bg-emerald-400"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className={labelClass}>Registration Role</label>
                        <select
                          value={inviteTargetRole}
                          onChange={(e) => {
                            const role = e.target.value as TargetRole;
                            setInviteTargetRole(role);
                            syncAccessTrackForRole(role);
                          }}
                          className={fieldClass}
                        >
                          <option value="CANDIDATE">Candidate (Executive)</option>
                          <option value="RECRUITER">Recruiter (Hiring Partner)</option>
                        </select>
                        <p className="mt-1 text-[10px] font-semibold text-slate-600">
                          The invitee only sees this one registration option.
                        </p>
                      </div>
                      <div>
                        <label className={labelClass}>Access Track</label>
                        <select
                          value={inviteAccessTrack}
                          onChange={(e) => setInviteAccessTrack(e.target.value as AccessTrack)}
                          className={fieldClass}
                        >
                          {accessTracksForRole(inviteTargetRole)
                            .filter((option) => isTrackEnabled(option.value))
                            .map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label} → {option.waitlistStatus}
                              </option>
                            ))}
                        </select>
                        <p className="mt-1 text-[10px] font-semibold text-slate-600">
                          {accessTracksForRole(inviteTargetRole).find((o) => o.value === inviteAccessTrack)?.description}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Personal Message (optional)</label>
                      <textarea
                        rows={3}
                        value={inviteMessage}
                        onChange={(e) => setInviteMessage(e.target.value)}
                        placeholder="Hi, we'd love for you to join Nexgent..."
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Link Validity (Hours)</label>
                      <input
                        type="number"
                        min={1}
                        value={inviteTtl}
                        onChange={(e) => setInviteTtl(Number(e.target.value) || 72)}
                        className={fieldClass}
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex gap-3">
                    <button
                      onClick={() => setIsInviteOpen(false)}
                      className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleInvite}
                      disabled={submitting}
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 text-sm font-black text-slate-950 transition hover:bg-amber-400 disabled:opacity-60"
                    >
                      {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                      Send Invitation
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default GrowthProspectsComponent;